<template>
  <n-menu ref="menu">
    <n-menu-group divider>
      <n-input
        v-model="linkValue"
        @keypress.native.enter="onEnter"
        ref="input"
        size="s"
        variableWidth
        :error="linkValidationFailed ? $t('linkMenu.invalid') : undefined"
      />
    </n-menu-group>
    <n-menu-group>
      <n-list-item leadingIcon="link" :text="$t('linkMenu.link')" @click="tryUpdateLink(linkValue)" />
      <n-list-item leadingIcon="external-link" :text="$t('linkMenu.open')" :disabled="!canOpen" @click="openLink()" />
      <n-list-item
        leadingIcon="document-duplicate"
        :text="$t('linkMenu.copy')"
        :disabled="!canCopy"
        @click="copyLink()"
      />
      <n-list-item leadingIcon="trash" :text="$t('linkMenu.remove')" :disabled="!canRemove" @click="removeLink()" />
    </n-menu-group>
  </n-menu>
</template>

<script lang="ts">
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import isURL from 'validator/lib/isURL'
import { mapStores } from 'pinia'
import { useSnackbarStore, SnackbarStore } from '@/modules/global/stores/SnackbarStore'
import NMenu from './core/NMenu/NMenu.vue'
import NInput from './core/NInput.vue'
import { writeTextToClipboard } from '@/util/dom-utils'
import { APP_LINK_SCHEME, normalizeLinkHref } from '@/util/link-href'
import { openInNewTabWithFeedback } from '@/util/open-in-new-tab'

@Component({
  computed: {
    ...mapStores(useSnackbarStore)
  }
})
export default class LinkInput extends Vue {
  @Prop() private link!: string
  private linkValue: string = ''
  private linkValidationFailed = false
  private snackbarStore!: SnackbarStore

  @Ref('menu') menuRef!: NMenu
  @Ref('input') inputRef!: NInput

  open(openFrom: HTMLElement) {
    this.linkValue = this.link
    this.menuRef.open(openFrom)
    // Without preventScroll the page jumps to the top: tippy has not placed the menu yet, so the field is still at the
    // top of <body> when it takes focus
    this.$nextTick(() => {
      this.inputRef.focus({ preventScroll: true })
    })
  }

  close() {
    this.menuRef.close()
  }

  @Watch('link') watchLink() {
    this.linkValue = this.link
  }

  @Watch('linkValue') watchLinkValue() {
    this.linkValidationFailed = !this.isValidLink
  }

  get isValidLink(): boolean {
    const value = this.linkValue.trim()
    // mailto:, tel: and sms: are not URLs to isURL, but they are links the editor opens and saves
    return (APP_LINK_SCHEME.test(value) && value.replace(APP_LINK_SCHEME, '').length > 0) || isURL(value)
  }

  /** Something to copy: a valid URL, or the saved link itself (mailto:, tel: and the like fail the URL check) */
  get canCopy(): boolean {
    return this.isValidLink || (!!this.link && this.linkValue === this.link)
  }

  /** Same test as Copy: a valid URL, or the saved link itself */
  get canOpen(): boolean {
    return this.canCopy
  }

  /** Only a link that is saved on the selection can be removed */
  get canRemove(): boolean {
    return !!this.link
  }

  tryUpdateLink() {
    if (this.isValidLink) {
      this.$emit('updateLink', this.linkValue)
      this.close()
    } else {
      this.linkValidationFailed = true
    }
  }

  onEnter(event: KeyboardEvent) {
    event.stopPropagation()
    event.preventDefault()
    setTimeout(() => {
      this.tryUpdateLink()
    }, 200)
  }

  copyLink(event?: MouseEvent) {
    if (event) {
      event.stopPropagation()
      event.preventDefault()
    }
    if (!this.canCopy) return

    writeTextToClipboard(Promise.resolve(this.linkValue)).then(this.onSuccessfulCopy)
  }

  openLink() {
    if (!this.canOpen) return
    if (!openInNewTabWithFeedback(normalizeLinkHref(this.linkValue))) return
    this.close()
  }

  onSuccessfulCopy() {
    this.snackbarStore.enqueue({
      label: this.$t('linkMenu.copied') as string,
      leadingIcon: true,
      variant: 'success'
    })
    this.close()
  }

  removeLink() {
    if (!this.canRemove) return
    this.$emit('removeLink')
    this.close()
  }
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles' as *;

.link-input {
  @include notch-style-floating;
  @include notch-text-label-strong-M;
  background-color: $notch-color-neutral-grey-000;
  border-radius: $notch-radius-2x;
}

.menu-entry {
  &:hover {
    background-color: $notch-color-neutral-grey-000;
    border-radius: $notch-radius-2x;
  }
}
</style>
